import { container } from 'tsyringe'
import Vue from 'vue'
import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators'
import { ExchangeOTPRequest, ForgotPasswordRequest, ForgotPasswordVerifyRequest, LoginApiRequest, ProfileMfaOtpRequest, RequestLibertaOTP, ResetPasswordRequest } from '@/data/payload/api/AuthRequest'
import store from '@/app/ui/store'
import { AuthPresenter } from '../presenters/AuthPresenter'
import { Account, OTP, Token } from '@/domain/entities/Account'
import { EventBus, EventBusConstants, LocalStorage, Utils } from '@/app/infrastructures/misc'
import router from '@/app/ui/router'
import { RawLocation } from 'vue-router'
import MainController from './MainController'
import { handleMfaRequestOtpError } from '@/app/infrastructures/misc/MfaErrorHandler'
import { recaptchaV3 } from '@/app/infrastructures/misc/RecaptchaV3'
import { syncUnleashUserContext } from '@/feature-flags'

export interface LoginState {
  isLoading: boolean
  isLoadingProfile: boolean
  isLoadingForgotPassword: boolean
  isLogin2faEnabled: boolean
  isLogin2faEnrolled: boolean
  isMfaAlertDismissed: boolean
  enrollmentOtpId: number
  enrollmentMaskedEmail: string
  enrollmentOtpExpiresInSeconds: number
  enrollmentCooldownIn: number
  mfaVerifyErrorMessage: string
}

@Module({ namespaced: true, store, name: 'login', dynamic: true })
class AuthController extends VuexModule implements LoginState {
  private get authPresenter(): AuthPresenter {
    return container.resolve(AuthPresenter)
  }
  public isLoading = false
  public isLoadingProfile = false
  public isLoadingForgotPassword = false
  public isLogin2faEnabled = false
  public isLogin2faEnrolled = false
  public isMfaAlertDismissed = false
  public enrollmentOtpId = 0
  public enrollmentMaskedEmail = ''
  public enrollmentOtpExpiresInSeconds = 0
  public enrollmentCooldownIn = 0
  public mfaVerifyErrorMessage = ''

  @Action({ rawError: true })
  public async getMe() {
    this.setLoadingProfile(true)

    this.authPresenter
      .getMe()
      .then(res => {
        this.setMyProfileData(res)
        router.replace(
          (router.currentRoute.query.redirect as RawLocation) || '/'
        )
      })
      .catch(error => {
        Vue.notify({
          title: 'Login Failed',
          text: 'Please Try Again',
          type: 'warn',
          duration: 3000,
        })
        console.error('Failed fetching', error)
      })
      .finally(() => {
        this.setLoadingProfile(false)
      })
  }

  @Action({ rawError: true })
  public sendLogin(params: { email: string; password: string }) {
    this.setLoading(true)

    container.resolve(AuthPresenter)
      .onLogin(new LoginApiRequest('CMS', params.email, params.password))
      .then(res => {
        if (res.requireChangePassword) {
          LocalStorage.setLocalStorage(LocalStorage.TOKEN_KEY, res.accessToken)
          return EventBus.$emit(EventBusConstants.REQUIRE_CHANGE_PASSWORD, Utils.parseJwt(res.accessToken));
        }
        this.setAuthData({ auth: res, email: params.email })
        this.getMe()
        MainController.connectShipmentWebsocket();
      })
      .catch(error => {
        Vue.notify({
          title: 'Login Failed',
          text:
            [400, 422, 401].includes(error.status) ? error.error.message.id : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public sendLogin2FA(params: { email: string; password: string }) {
    this.setLoading(true)

    container.resolve(AuthPresenter)
      .onLogin2FA(new LoginApiRequest('CMS', params.email, params.password))
      .then(res => {
        if (res.authentication?.requireChangePassword) {
          LocalStorage.setLocalStorage(LocalStorage.TOKEN_KEY, res.authentication.accessToken)
          return EventBus.$emit(EventBusConstants.REQUIRE_CHANGE_PASSWORD, Utils.parseJwt(res.authentication.accessToken));
        }
        if (!res.twoFactorAuth.enrollment.enrolled && res.twoFactorAuth.enrollment.setupRequired) {
          return EventBus.$emit(EventBusConstants.REQUIRE_OTP, res);
        }
        if (res.twoFactorAuth.enrollment && res.twoFactorAuth.otpRequired) {
          return EventBus.$emit(EventBusConstants.PROCEED_OTP_2FA, res);
        }
        if (res.authentication?.accessToken) {
          this.setAuthData({ auth: res.authentication, email: params.email })
          this.getMe()
          MainController.connectShipmentWebsocket();
        }
      })
      .catch(error => {
        Vue.notify({
          title: 'Login Failed',
          text:
            [400, 422, 401].includes(error.status) ? error.error.message.id : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public handleSkipOtp(params: {token: Token, email: string}): void {
    this.setAuthData({ auth: params.token, email: params.email })
    this.getMe()
    MainController.connectShipmentWebsocket();
  }

  @Action({ rawError: true })
  public forgotPassword(params: ForgotPasswordRequest) {
    this.setLoading(true)

    container.resolve(AuthPresenter)
      .forgotPassword(params)
      .then(() => {
        EventBus.$emit(EventBusConstants.FORGOT_PASSWORD_SUCCESS, {
          email: params.email,
        })
      })
      .catch(error => {
        if (error.status === 403) {
          EventBus.$emit(EventBusConstants.FORGOT_PASSWORD_LIMIT)

          return
        }

        Vue.notify({
          title: 'Forgot Password',
          text: error.error.message.en,
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public forgotPasswordVerify(token: string) {
    this.setLoadingForgotPassword(true)

    this.authPresenter
      .forgotPasswordVerify(new ForgotPasswordVerifyRequest(token))
      .catch(error => {
        router.replace({
          name: 'ResetPassword',
          query: {
            status: 'invalid',
            message: error.error.message.en
          }
        })
      })
      .finally(() => {
        this.setLoadingForgotPassword(false)
      })
  }

  @Action({ rawError: true })
  public resetPassword(form: { token: string, password: string, passwordConfirmation: string }) {
    this.setLoading(true)

    this.authPresenter
      .resetPassword(new ResetPasswordRequest(form.token, form.password, form.passwordConfirmation))
      .then(() => {
        EventBus.$emit(EventBusConstants.RESET_PASSWORD_SUCCESS)
      })
      .catch(error => {
        Vue.notify({
          title: 'Reset Password',
          text: error.error.message.en,
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public requestLibertaOTP(payload: RequestLibertaOTP): Promise<OTP> {
    return container.resolve(AuthPresenter).requestLibertaOTP(payload);
  }

  @Action({ rawError: true })
  public exchangeOTP(payload: ExchangeOTPRequest): Promise<string> {
    return container.resolve(AuthPresenter).exchangeOTP(payload);
  }

  @Action({ rawError: true })
  public dismissMfaEnrollmentAlert() {
    this.setMfaAlertDismissed(true)
  }

  @Action({ rawError: true })
  public async navigateToMfaEnrollment(): Promise<void> {
    this.setLoading(true)
    try {
      const captcha = await recaptchaV3.executeRecaptcha(
        'login_2fa_enroll'
      )
      const response =
        await this.authPresenter.requestProfileMfaOtp(
          new ProfileMfaOtpRequest({
            captcha,
          })
        )

      this.setEnrollmentVerifyState({
        otpId: response.otpId,
        maskedEmail: response.username,
        expiredIn: response.expiredIn,
        cooldownIn: response.cooldownIn,
      })
      this.dismissMfaEnrollmentAlert()

      router.push({
        name: 'AccountMfaVerifyOtp',
      })
    } catch (error) {
      await handleMfaRequestOtpError(
        error,
        () => this.refreshProfile()
      )
    } finally {
      this.setLoading(false)
    }
  }

  @Action({ rawError: true })
  public async refreshProfile(): Promise<void> {
    this.setLoadingProfile(true)

    try {
      const res = await this.authPresenter.getMe()
      this.setMyProfileData(res)
    } finally {
      this.setLoadingProfile(false)
    }
  }

  @Action({ rawError: true })
  public async verifyMfaEnrollment(
    payload: ExchangeOTPRequest
  ): Promise<void> {
    this.setLoading(true)
    try {
      await this.authPresenter.verifyProfileMfaOtp(payload)
      await this.refreshProfile()
      this.clearEnrollmentVerifyState()
      Vue.notify({
        type: 'success',
        text: 'Verifikasi OTP berhasil diaktifkan',
      })
      router.push({
        name: 'DeviceManagerPage',
      })
    } catch (error: any) {
      console.log(error)
      const status = error?.data?.error_id

      const message =
        error?.data?.message?.id ||
        'Verifikasi gagal'

      switch (status) {
        case 400:
          this.setMfaVerifyError(message)
          break

        case 422:
          this.setMfaVerifyError(message)
          break

        default:
          Vue.notify({
            type: 'error',
            text: message,
          })
      }
    } finally {
      this.setLoading(false)
    }
  }

  @Action({ rawError: true })
  async resendMfaEnrollmentOtp(): Promise<void> {
    this.setLoading(true)
    try {
      const captcha = await recaptchaV3.executeRecaptcha(
        'login_2fa_enroll'
      )
      const response =
        await this.authPresenter.requestProfileMfaOtp(
          new ProfileMfaOtpRequest({
            captcha,
          })
        )

      this.setEnrollmentVerifyState({
        otpId: response.otpId,
        maskedEmail: response.username,
        expiredIn: response.expiredIn,
        cooldownIn: response.cooldownIn,
      })
    } catch (error) {
      await handleMfaRequestOtpError(
        error,
        () => this.refreshProfile()
      )
    } finally {
      this.setLoading(false)
    }
  }

  @Mutation
  public setAuthData(data: { auth: Token; email: string }) {
    LocalStorage.setLocalStorage(LocalStorage.TOKEN_KEY, data.auth.accessToken)
    LocalStorage.setLocalStorage(
      LocalStorage.TOKEN_EXPIRED,
      data.auth.expiredAt
    )
    LocalStorage.setLocalStorage(LocalStorage.LOGIN_IDENTITY, data.email, true)
  }

  @Mutation
  private setMyProfileData(data: Account) {
    LocalStorage.setLocalStorage(LocalStorage.MY_ROLE, data.role, true)
    LocalStorage.setLocalStorage(LocalStorage.ACCOUNT_ID, data.id, true)
    LocalStorage.setLocalStorage(
      LocalStorage.ACCESS_MENU,
      data.accessMenus?.map(item => item.slug),
      true
    )
    LocalStorage.setLocalStorage(LocalStorage.LOGIN_IDENTITY, data.email, true)
    syncUnleashUserContext({ email: data.email })
    this.isLogin2faEnabled = !!data.isLogin2faEnabled
    this.isLogin2faEnrolled = !!data.isLogin2faEnrolled
  }

  @Mutation
  private setLoading(bool: boolean) {
    this.isLoading = bool
  }

  @Mutation
  private setLoadingForgotPassword(bool: boolean) {
    this.isLoadingForgotPassword = bool
  }

  @Mutation
  private setLoadingProfile(bool: boolean) {
    this.isLoadingProfile = bool
  }

  @Mutation
  private setMfaAlertDismissed(value: boolean) {
    this.isMfaAlertDismissed = value
  }

  @Mutation
  private setEnrollmentVerifyState(payload: {
    otpId: number
    maskedEmail: string
    expiredIn: number
    cooldownIn: number
  }) {
    this.enrollmentOtpId = payload.otpId
    this.enrollmentMaskedEmail = payload.maskedEmail
    this.enrollmentOtpExpiresInSeconds = payload.expiredIn
    this.enrollmentCooldownIn = payload.cooldownIn
  }

  @Mutation
  public clearEnrollmentVerifyState() {
    this.enrollmentOtpId = 0
    this.enrollmentMaskedEmail = ''
    this.enrollmentOtpExpiresInSeconds = 0
    this.enrollmentCooldownIn = 0
  }

  @Mutation
  private setMfaVerifyError(
    message: string
  ): void {
    this.mfaVerifyErrorMessage = message
  }

  @Mutation
  resetMfaEnrollmentState() {
    this.isMfaAlertDismissed = false
    this.enrollmentOtpId = 0
    this.enrollmentMaskedEmail = ''
    this.enrollmentOtpExpiresInSeconds = 0
    this.enrollmentCooldownIn = 0
    this.mfaVerifyErrorMessage = ''
  }

  get showMfaEnrollmentAlert(): boolean {
    return (
      this.isLogin2faEnabled &&
      !this.isLogin2faEnrolled &&
      !this.isMfaAlertDismissed
    )
  }

  get enrollmentSessionActive(): boolean {
    return !!this.enrollmentOtpId
  }

  @Action({ rawError: true })
  request2FAOTP(payload: RequestLibertaOTP) {
    const presenter = container.resolve(AuthPresenter)
    return presenter.request2FAOTP(payload);
  }

  @Action({ rawError: true })
  verify2FAOTP(payload: ExchangeOTPRequest) {
    const presenter = container.resolve(AuthPresenter);
    return presenter.verify2FAOTP(payload)
  }
}

export default getModule(AuthController)
